import React, { useState, useEffect } from "react";
import { db } from "../firebase";
import { ref, onValue } from "firebase/database";

export default function AdminDashboard({ onLogout, darkMode, setDarkMode }) {
  const [usersList, setUsersList] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [systemStats, setSystemStats] = useState({
    total: 0,
    completed: 0,
    pending: 0,
    overdue: 0,
  });

  useEffect(() => {
    const usersRef = ref(db, "users");
    const unsubscribe = onValue(usersRef, (snapshot) => {
      if (snapshot.exists()) {
        const data = snapshot.val();
        let total = 0,
          completed = 0,
          pending = 0,
          overdue = 0;
        const now = new Date();

        const formattedUsers = Object.entries(data).map(([uid, values]) => {
          const userTasks = values.tasks
            ? Object.entries(values.tasks).map(([id, t]) => {
                const task = { id, ...t };
                total++;

                // Calculate Stats
                if (task.status === "Completed") {
                  completed++;
                } else {
                  pending++;
                  // Check for overdue (combining date and time)
                  const taskDate = new Date(
                    `${task.dueDate}T${task.dueTime || "00:00"}`
                  );
                  if (taskDate < now) overdue++;
                }
                return task;
              })
            : [];

          return {
            uid,
            email: values.profile?.email || "No Email",
            displayName: values.profile?.displayName || "Anonymous",
            photo: values.profile?.photo || null,
            provider: values.profile?.provider || "email",
            tasks: userTasks,
          };
        });

        setSystemStats({ total, completed, pending, overdue });
        setUsersList(formattedUsers);
      }
      setLoading(false);
    });
    return () => unsubscribe();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-slate-950 flex items-center justify-center">
        <div className="w-10 h-10 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950 p-6 md:p-10 transition-colors">
      <div className="max-w-7xl mx-auto">
        {/* HEADER */}
        <div className="flex justify-between items-center mb-10">
          <div>
            <h1 className="text-3xl font-black dark:text-white uppercase tracking-tighter">
              Admin Overview
            </h1>
            <p className="text-slate-500 text-xs font-bold uppercase tracking-widest">
              Platform Pulse & User Activity
            </p>
          </div>
          <div className="flex gap-4">
            <button
              onClick={() => setDarkMode(!darkMode)}
              className="p-3 rounded-xl bg-white dark:bg-slate-900 shadow-sm text-slate-500 border border-slate-100 dark:border-slate-800"
            >
              <i className={`fas ${darkMode ? "fa-sun" : "fa-moon"}`}></i>
            </button>
            <button
              onClick={onLogout}
              className="px-6 py-2 bg-red-500 text-white rounded-xl font-bold text-xs uppercase shadow-lg shadow-red-500/20 active:scale-95 transition-all"
            >
              Logout
            </button>
          </div>
        </div>

        {/* STATS CARDS */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10">
          <StatCard
            label="Total Tasks"
            value={systemStats.total}
            color="indigo"
            icon="fa-tasks"
          />
          <StatCard
            label="Completed"
            value={systemStats.completed}
            color="emerald"
            icon="fa-check-circle"
          />
          <StatCard
            label="Pending"
            value={systemStats.pending}
            color="amber"
            icon="fa-pause-circle"
          />
          <StatCard
            label="Overdue"
            value={systemStats.overdue}
            color="red"
            icon="fa-exclamation-triangle"
          />
        </div>

        {/* USER DIRECTORY */}
        <h3 className="text-[10px] font-black uppercase text-slate-400 tracking-[0.2em] mb-4">
          Registered Users ({usersList.length})
        </h3>
        <div className="bg-white dark:bg-slate-900 rounded-[2rem] shadow-sm overflow-hidden border border-slate-100 dark:border-slate-800">
          <div className="overflow-x-auto">
            <table className="w-full text-left">
              <thead className="bg-slate-50 dark:bg-slate-800/50">
                <tr>
                  <th className="p-6 text-[10px] font-black uppercase text-slate-400">
                    User Details
                  </th>
                  <th className="p-6 text-[10px] font-black uppercase text-slate-400">
                    Auth Method
                  </th>
                  <th className="p-6 text-[10px] font-black uppercase text-slate-400 text-right">
                    Tasks
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-50 dark:divide-slate-800/50">
                {usersList.map((user) => (
                  <tr
                    key={user.uid}
                    onClick={() => setSelectedUser(user)}
                    className="hover:bg-slate-50 dark:hover:bg-slate-800/80 cursor-pointer transition-all group"
                  >
                    <td className="p-6 flex items-center gap-4">
                      <div className="relative">
                        <img
                          src={
                            user.photo ||
                            `https://ui-avatars.com/api/?name=${user.displayName}&background=6366f1&color=fff`
                          }
                          className="w-11 h-11 rounded-2xl border-2 border-white dark:border-slate-700 shadow-sm object-cover"
                          alt=""
                        />
                        <div className="absolute -bottom-1 -right-1 w-4 h-4 bg-emerald-500 border-2 border-white dark:border-slate-900 rounded-full"></div>
                      </div>
                      <div>
                        <p className="font-bold dark:text-white text-sm group-hover:text-indigo-600 transition-colors">
                          {user.displayName}
                        </p>
                        <p className="text-[10px] text-slate-500 font-medium">
                          {user.email}
                        </p>
                      </div>
                    </td>
                    <td className="p-6">
                      <span
                        className={`px-3 py-1 rounded-lg text-[9px] font-black uppercase ${
                          user.provider === "google.com"
                            ? "bg-orange-50 text-orange-600 border border-orange-100"
                            : "bg-slate-100 text-slate-600 border border-slate-200"
                        }`}
                      >
                        {user.provider === "google.com"
                          ? "Google Account"
                          : "Email / Pass"}
                      </span>
                    </td>
                    <td className="p-6 text-right">
                      <span className="bg-indigo-50 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400 px-3 py-1 rounded-full font-black text-xs">
                        {user.tasks.length}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* DRAWER MODAL (User Details) */}
        {selectedUser && (
          <UserModal
            user={selectedUser}
            onClose={() => setSelectedUser(null)}
          />
        )}
      </div>
    </div>
  );
}

function StatCard({ label, value, color, icon }) {
  const themes = {
    indigo:
      "text-indigo-600 bg-white border-slate-100 dark:bg-slate-900 dark:border-slate-800",
    emerald:
      "text-emerald-600 bg-white border-slate-100 dark:bg-slate-900 dark:border-slate-800",
    amber:
      "text-amber-600 bg-white border-slate-100 dark:bg-slate-900 dark:border-slate-800",
    red: "text-red-600 bg-white border-slate-100 dark:bg-slate-900 dark:border-slate-800",
  };

  const iconColors = {
    indigo: "bg-indigo-50 text-indigo-600",
    emerald: "bg-emerald-50 text-emerald-600",
    amber: "bg-amber-50 text-amber-600",
    red: "bg-red-50 text-red-600",
  };

  return (
    <div
      className={`p-6 rounded-[2rem] border shadow-sm transition-transform hover:scale-[1.02] ${themes[color]}`}
    >
      <div
        className={`w-10 h-10 rounded-xl flex items-center justify-center mb-4 ${iconColors[color]}`}
      >
        <i className={`fas ${icon} text-sm`}></i>
      </div>
      <p className="text-[10px] font-black uppercase text-slate-400 tracking-widest">
        {label}
      </p>
      <h3 className="text-3xl font-black mt-1 dark:text-white">{value}</h3>
    </div>
  );
}

function UserModal({ user, onClose }) {
  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-slate-900/40 backdrop-blur-sm transition-opacity">
      {/* Click outside to close */}
      <div className="absolute inset-0" onClick={onClose}></div>

      <div className="relative w-full max-w-lg h-full bg-white dark:bg-slate-950 shadow-2xl p-8 md:p-10 overflow-y-auto animate-in slide-in-from-right duration-500">
        <button
          onClick={onClose}
          className="mb-8 w-10 h-10 flex items-center justify-center rounded-full bg-slate-100 dark:bg-slate-800 text-slate-500 hover:bg-indigo-600 hover:text-white transition-all"
        >
          <i className="fas fa-times"></i>
        </button>

        <div className="flex items-center gap-6 mb-10">
          <img
            src={
              user.photo ||
              `https://ui-avatars.com/api/?name=${user.displayName}`
            }
            className="w-20 h-20 rounded-[2rem] shadow-xl border-4 border-white dark:border-slate-800"
            alt=""
          />
          <div>
            <h2 className="text-2xl font-black dark:text-white tracking-tighter uppercase">
              {user.displayName}
            </h2>
            <p className="text-sm font-bold text-indigo-600">{user.email}</p>
          </div>
        </div>

        <div className="space-y-6">
          <div className="flex justify-between items-center">
            <h3 className="text-[10px] font-black uppercase text-slate-400 tracking-[0.2em]">
              Task Roadmap
            </h3>
            <span className="text-[10px] font-bold text-slate-400">
              {user.tasks.length} Total
            </span>
          </div>

          {user.tasks.length > 0 ? (
            <div className="space-y-3">
              {user.tasks.map((t) => (
                <div
                  key={t.id}
                  className="p-5 rounded-3xl bg-slate-50 dark:bg-slate-900/50 border border-slate-100 dark:border-slate-800 transition-all hover:border-indigo-500/30"
                >
                  <div className="flex justify-between items-start mb-2">
                    <h4 className="font-bold text-sm dark:text-white pr-4">
                      {t.title}
                    </h4>
                    <span
                      className={`text-[8px] font-black px-2 py-1 rounded uppercase ${
                        t.status === "Completed"
                          ? "bg-emerald-100 text-emerald-600"
                          : "bg-amber-100 text-amber-600"
                      }`}
                    >
                      {t.status}
                    </span>
                  </div>
                  <div className="flex items-center gap-4 text-[10px] text-slate-400 font-bold uppercase tracking-tighter">
                    <span>
                      <i className="far fa-calendar-alt mr-1"></i> {t.dueDate}
                    </span>
                    <span>
                      <i className="far fa-clock mr-1"></i>{" "}
                      {t.dueTime || "No Time"}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-10 bg-slate-50 dark:bg-slate-900/50 rounded-3xl border border-dashed border-slate-200 dark:border-slate-800">
              <i className="fas fa-folder-open text-slate-300 mb-3 text-2xl"></i>
              <p className="text-[10px] font-black text-slate-400 uppercase">
                No tasks recorded
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
